import React, { useState, useEffect } from "react";
import { changePassword } from "shared/api/client/orvalHelpers";
import { parseApiError } from "shared/api/client/errorParser";
import "styles/legacy/change-password-modal.css";

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
  setSuccessMessage: (msg: string) => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  open,
  onClose,
  setSuccessMessage,
}) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (!open) {
      setCurrentPassword("");
      setNewPassword("");
      setRepeatPassword("");
      setErrors({});
    }
  }, [open]);

  if (!open) return null;

  const validatePassword = (password: string) => {
    const errors: string[] = [];
    if (password.length < 8)
      errors.push("Password must be at least 8 characters.");
    if (!/[A-Z]/.test(password))
      errors.push("Must contain at least one capital letter.");
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password))
      errors.push("Must contain at least one special character.");
    if (!/[0-9]/.test(password)) {
      errors.push("Password must contain at least one number.");
    }
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: { [key: string]: string } = {};

    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      newErrors.newPassword = passwordErrors.join(" ");
    }

    if (newPassword !== repeatPassword) {
      newErrors.repeatPassword = "Passwords do not match.";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      try {
        await changePassword(currentPassword, newPassword);

        setSuccessMessage("Password changed successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
        onClose();
      } catch (error: any) {
        const message = parseApiError(error, "Failed to change password.");
        let newErrors: { [key: string]: string } = {};

        if (
          message.includes(
            "The new password cannot be the same as the current password"
          )
        ) {
          newErrors.repeatPassword =
            "The new password cannot be the same as the current password";
        } else if (
          message.includes("Current password is incorrect")
        ) {
          newErrors.repeatPassword = "Current password is incorrect";
        } else {
          newErrors.api = message;
        }

        setErrors(newErrors);
        console.error(error);
      }
    }
  };

  return (
    <div className="change-password-modal-overlay" onClick={onClose}>
      <div
        className="change-password-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="change-password-top-div">
          <div className="change-password-modal-title">Change Password</div>
          <button className="change-password-modal-close-btn" onClick={onClose}>
            <img
              src="/images/icons/close-button-change-password-modal.svg"
              alt="close button"
              style={{ cursor: "pointer" }}
            />
          </button>
        </div>
        <div className="modal-divider" />
        <div className="change-password-mid-div">
          <form className="change-password-form" onSubmit={handleSubmit}>
            {errors.api && <div className="change-password-error" role="alert">{errors.api}</div>}
            <div className="change-password-modal-form-group">
              <label className="change-password-modal-label">
                Current Password*
              </label>
              <input
                className="change-password-modal-input"
                type="password"
                placeholder="Enter current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </div>
            <div className="change-password-modal-form-group">
              <label className="change-password-modal-label">
                New Password*
              </label>
              <input
                className="change-password-modal-input"
                type="password"
                placeholder="Enter a new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              {errors.newPassword && (
                <div className="change-password-error">
                  {errors.newPassword}
                </div>
              )}
              <div className="change-password-modal-format">
                8 characters minimum, at least 1 capital letter, 1 number and 1
                special character
              </div>
            </div>
            <div className="change-password-modal-form-group">
              <label className="change-password-modal-label">
                Repeat Password*
              </label>
              <input
                className="change-password-modal-input"
                type="password"
                placeholder="Repeat new password"
                value={repeatPassword}
                onChange={(e) => setRepeatPassword(e.target.value)}
              />
              {errors.repeatPassword && (
                <div className="change-password-error">
                  {errors.repeatPassword}
                </div>
              )}
            </div>
            <div className="modal-divider" />
            <div className="modal-actions">
              <button type="button" className="cancel-button" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="reset-button">
                Change password
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
